import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ExternalLink,
  FileLock2,
  Info,
  Loader2,
  ScrollText,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import {
  useCompany,
  useDeleteCert,
  useDeleteCompany,
  useDianProcesses,
} from "@/lib/api/companies.queries";
import { useActiveCompany } from "@/lib/company-context";
import {
  useCanAdministerCompanies,
  useCanDeleteCertificate,
  useCanWriteCompanies,
} from "@/lib/team-context";
import { CompanyOnboardingProgress } from "@/components/companies/CompanyOnboardingProgress";
import { CompanyEnrollmentWizard } from "@/components/companies/CompanyEnrollmentWizard";
import { CompanyLogoCard } from "@/components/companies/CompanyLogoCard";
import { translateCompanyError } from "@/components/companies/error-translations";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/companies/$companyId/")({
  component: CompanyDetailPage,
});

const ENV_VARIANT: Record<string, "muted" | "success"> = {
  hab: "muted",
  prod: "success",
};

function CompanyDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CompanyDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function CompanyDetailContent() {
  const { companyId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  const companyQuery = useCompany(companyId);
  const deleteCompany = useDeleteCompany();
  const canEdit = useCanWriteCompanies();
  const canArchive = useCanAdministerCompanies();
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Sync the URL companyId into the active context so the switcher reflects it.
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (companyQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (companyQuery.isError || !companyQuery.data) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title={t("companies.detail.error.title")}
          description={t("companies.detail.error.body")}
          onRetry={() => void companyQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      </div>
    );
  }

  const company = companyQuery.data;
  const envVariant = ENV_VARIANT[company.dianRuntimeEnv] ?? "muted";

  function handleArchive() {
    deleteCompany.mutate(companyId, {
      onSuccess: () => {
        toast.success(t("companies.detail.archive.toast.success"));
        setArchiveOpen(false);
        void navigate({ to: "/companies" });
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code) {
          const translated = translateCompanyError(err.code, t);
          toast.error(translated ?? err.message);
          return;
        }
        toast.error(err instanceof Error ? err.message : t("common.error"));
      },
    });
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Link
          to="/companies"
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("companies.detail.back")}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {company.legalName}
              </h1>
              <Badge variant={envVariant} className="text-[10px] uppercase">
                {t(`companies.env.${company.dianRuntimeEnv}`)}
              </Badge>
            </div>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {company.identificationType} {company.nit}
              {company.dv ? `-${company.dv}` : null}
            </p>
            {company.tradeName ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {company.tradeName}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {canEdit ? (
              <Button asChild variant="outline">
                <Link to="/companies/$companyId/edit" params={{ companyId }}>
                  {t("companies.detail.edit_cta")}
                </Link>
              </Button>
            ) : null}
            {canArchive ? (
              <Button
                variant="outline"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setArchiveOpen(true)}
              >
                <Trash2 className="h-4 w-4" />
                {t("companies.detail.archive_cta")}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="py-4">
          <CompanyOnboardingProgress status={company.onboardingStatus} />
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" className="gap-2">
            <Info className="h-3.5 w-3.5" />
            {t("companies.detail.tab.overview")}
          </TabsTrigger>
          <TabsTrigger value="cert" className="gap-2">
            <FileLock2 className="h-3.5 w-3.5" />
            {t("companies.detail.tab.cert")}
          </TabsTrigger>
          <TabsTrigger value="processes" className="gap-2">
            <ScrollText className="h-3.5 w-3.5" />
            {t("companies.detail.tab.processes")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab company={company} />
        </TabsContent>

        <TabsContent value="cert">
          <CertTab companyId={companyId} certUploaded={company.certUploaded} />
        </TabsContent>

        <TabsContent value="processes">
          <ProcessesTab companyId={companyId} />
        </TabsContent>
      </Tabs>

      <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("companies.detail.archive.confirm_title")}
            </DialogTitle>
            <DialogDescription>
              {t("companies.detail.archive.confirm_body", {
                name: company.legalName,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setArchiveOpen(false)}
              disabled={deleteCompany.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleArchive}
              disabled={deleteCompany.isPending}
            >
              {deleteCompany.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {t("companies.detail.archive.confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OverviewTab({
  company,
}: {
  company: ReturnType<typeof useCompany>["data"];
}) {
  const { t } = useTranslation();
  if (!company) return null;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("companies.detail.overview.title")}
          </CardTitle>
          <CardDescription>
            {t("companies.detail.overview.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("companies.detail.field.identification_type")}
              value={company.identificationType}
            />
            <Field
              label={t("companies.detail.field.regime")}
              value={
                company.regimeCode
                  ? t(`companies.regime.${company.regimeCode}`, company.regimeCode)
                  : "—"
              }
            />
            <Field
              label={t("companies.detail.field.address")}
              value={
                [company.addressLine1, company.addressLine2]
                  .filter(Boolean)
                  .join(", ") || "—"
              }
            />
            <Field
              label={t("companies.detail.field.phone")}
              value={company.phone || "—"}
            />
            <Field
              label={t("companies.detail.field.email")}
              value={company.email || "—"}
            />
            <Field
              label={t("companies.detail.field.responsibilities")}
              value={
                company.responsibilities.length > 0
                  ? company.responsibilities.join(", ")
                  : "—"
              }
            />
            <Field
              label={t("companies.detail.field.large_taxpayer")}
              value={company.isLargeTaxpayer ? t("common.yes") : t("common.no")}
            />
            <Field
              label={t("companies.detail.field.self_withholder")}
              value={company.isSelfWithholder ? t("common.yes") : t("common.no")}
            />
          </dl>
        </CardContent>
      </Card>
      <CompanyLogoCard companyId={company.id} company={company} />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}

function CertTab({
  companyId,
  certUploaded,
}: {
  companyId: string;
  certUploaded: boolean;
}) {
  const { t } = useTranslation();
  const companyQuery = useCompany(companyId);
  const deleteCert = useDeleteCert(companyId);
  const canDeleteCert = useCanDeleteCertificate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!companyQuery.data) return null;
  const company = companyQuery.data;
  const meta = company.certMeta;

  function handleDelete() {
    deleteCert.mutate(undefined, {
      onSuccess: () => {
        toast.success(t("companies.cert.delete.toast.success"));
        setConfirmDelete(false);
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code) {
          const translated = translateCompanyError(err.code, t);
          toast.error(translated ?? err.message);
          return;
        }
        toast.error(err instanceof Error ? err.message : t("common.error"));
      },
    });
  }

  if (!certUploaded) {
    return <CompanyEnrollmentWizard companyId={companyId} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">
                {t("companies.cert.installed.title")}
              </CardTitle>
              <CardDescription>
                {t("companies.cert.installed.subtitle")}
              </CardDescription>
            </div>
            {company.certUploadedAt ? (
              <p className="text-xs text-muted-foreground">
                {t("companies.cert.installed.uploaded_at", {
                  date: new Date(company.certUploadedAt).toLocaleString(),
                })}
              </p>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {company.certError ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              {t(`companies.cert.error.${company.certError}`, {
                defaultValue: t("companies.cert.error.dian_lookup"),
              })}
            </div>
          ) : meta ? (
            <dl className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t("companies.cert.installed.alias")}
                value={meta.alias}
              />
              <Field
                label={t("companies.cert.installed.has_password")}
                value={meta.hasPassword ? t("common.yes") : t("common.no")}
              />
              <Field
                label={t("companies.cert.installed.has_certificate")}
                value={meta.hasCertificate ? t("common.yes") : t("common.no")}
              />
              <Field
                label={t("companies.cert.installed.test_set_id")}
                value={meta.testSetId ?? "—"}
              />
              {(["softwareInvoice", "softwarePos", "softwarePayroll"] as const).map(
                (k) => {
                  const profile = meta[k];
                  return (
                    <div key={k} className="sm:col-span-2">
                      <p className="text-xs font-semibold uppercase text-muted-foreground">
                        {t(`companies.cert.profile.${k}`)}
                      </p>
                      <p className="mt-1 text-sm font-mono">
                        {profile.softwareId}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("companies.cert.installed.ranges_count", {
                          count: profile.resolutionRanges.length,
                        })}
                      </p>
                    </div>
                  );
                },
              )}
            </dl>
          ) : null}
        </CardContent>
      </Card>

      {canDeleteCert ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("companies.cert.actions.title")}
            </CardTitle>
            <CardDescription>
              {t("companies.cert.actions.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-4 w-4" />
              {t("companies.cert.delete.cta")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("companies.cert.delete.title")}</DialogTitle>
            <DialogDescription>
              {t("companies.cert.delete.body")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              disabled={deleteCert.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteCert.isPending}
            >
              {deleteCert.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {t("companies.cert.delete.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PROCESS_STATUS_VARIANT: Record<
  string,
  "default" | "success" | "warning" | "destructive" | "muted"
> = {
  queued: "muted",
  processing: "warning",
  completed: "success",
  failed: "destructive",
  pass: "success",
  fail: "destructive",
  timeout: "warning",
};

function ProcessesTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const processesQuery = useDianProcesses(companyId);

  if (processesQuery.isPending) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (processesQuery.isError) {
    return (
      <ErrorState
        title={t("dian.processes.error.title")}
        description={t("dian.processes.error.body")}
        onRetry={() => void processesQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const processes = processesQuery.data?.processes ?? [];

  if (processes.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <ScrollText className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("dian.processes.empty")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("dian.processes.col.created")}</TableHead>
              <TableHead>{t("dian.processes.col.operation")}</TableHead>
              <TableHead>{t("dian.processes.col.document_type")}</TableHead>
              <TableHead>{t("dian.processes.col.status")}</TableHead>
              <TableHead>{t("dian.processes.col.dian_status")}</TableHead>
              <TableHead>{t("dian.processes.col.identifier")}</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {processes.map((p) => {
              const variant = PROCESS_STATUS_VARIANT[p.status] ?? "muted";
              const dianVariant =
                PROCESS_STATUS_VARIANT[p.dianStatus ?? ""] ?? "muted";
              const ident = p.cufe ?? p.cude ?? p.cuds ?? p.cune;
              return (
                <TableRow key={p.processId}>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(p.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="font-mono">{p.operation}</span>
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {p.documentType}
                  </TableCell>
                  <TableCell>
                    <Badge variant={variant} className="text-[10px] uppercase">
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {p.dianStatus ? (
                      <Badge
                        variant={dianVariant}
                        className="text-[10px] uppercase"
                      >
                        {p.dianStatus}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "max-w-[160px] truncate font-mono text-[11px]",
                      ident ? "" : "text-muted-foreground",
                    )}
                    title={ident ?? ""}
                  >
                    {ident ?? "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {p.dianTrackId ? (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <ExternalLink className="h-3 w-3" />
                        {p.dianTrackId.slice(0, 8)}
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
